import { normalizeRoomCode } from "@arena/shared";

export const RESOLUTION_PROFILES = ["low", "medium", "high", "native"] as const;
export type ResolutionProfile = (typeof RESOLUTION_PROFILES)[number];

/** Vertical render resolution (game pixels) per profile; native = no pixelation. */
export const RESOLUTION_LINES: Record<ResolutionProfile, number | null> = {
  low: 180,
  medium: 270,
  high: 360,
  native: null,
};

/** How far in the past remote fighters are drawn, in ms (≈ 3 server ticks). */
export const INTERPOLATION_DELAY_MS = 100;

export interface Settings {
  diorama:boolean;
  name: string;
  /** Room code from a shared link (`?r=K7QPM`), already normalized; "" if none. */
  roomCode: string;
  resolution: ResolutionProfile;
  /** Debug panel + network simulator (`?debug`, implied by `?lag` / `?jitter`). */
  debug: boolean;
  lagMs: number;
  jitterMs: number;
  /** "2d" draws the game as it is (HD-2D sprites, D33); "3d" the old placeholder bodies. `?look=` wins over the build default (`VITE_DEFAULT_LOOK`). */
  look: "3d" | "2d";
}

const STORAGE_NAME = "arena.name";
const STORAGE_RESOLUTION = "arena.resolution";
/** Per tab: a reload resumes the same seat, a new tab is a new player. */
const SESSION_RESUME = "arena.resume";

export function loadSettings(): Settings {
  const params = new URLSearchParams(location.search);
  const lagMs = numberParam(params, "lag");
  const jitterMs = numberParam(params, "jitter");
  const wanted = params.get("look") ?? (import.meta.env.VITE_DEFAULT_LOOK as string | undefined) ?? "2d";
  const look = wanted === "3d" ? "3d" : "2d";
  return {
    diorama:readStorage('arena.diorama')!=='0',
    look,
    name: readStorage(STORAGE_NAME) ?? params.get("name") ?? "",
    roomCode: normalizeRoomCode(params.get("r") ?? ""),
    // The HD-2D prototype pixelates its sprites itself: the scene is drawn at full resolution.
    resolution: asProfile(params.get("res")) ?? (look === "2d" ? "native" : undefined) ?? asProfile(readStorage(STORAGE_RESOLUTION)) ?? "medium",
    debug: params.has("debug") || lagMs > 0 || jitterMs > 0,
    lagMs,
    jitterMs,
  };
}

export function saveResolution(profile: ResolutionProfile): void {
  writeStorage(STORAGE_RESOLUTION, profile);
}

export function saveName(name: string): void {
  writeStorage(STORAGE_NAME, name);
}

export function saveDiorama(enabled:boolean):void {writeStorage('arena.diorama',enabled?'1':'0');}

/** Reconnection token of the room this tab is in, so a reload resumes the same seat. */
export function saveResumeToken(token: string): void {
  try {
    sessionStorage.setItem(SESSION_RESUME, token);
  } catch {
    /* ignore */
  }
}

export function loadResumeToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_RESUME);
  } catch {
    return null;
  }
}

export function clearResumeToken(): void {
  try {
    sessionStorage.removeItem(SESSION_RESUME);
  } catch {
    /* ignore */
  }
}

/** Puts the room code in the address bar (or removes it), keeping the other parameters. */
export function setRoomInUrl(code: string | null): void {
  const url = new URL(location.href);
  if (code) url.searchParams.set("r", code);
  else url.searchParams.delete("r");
  history.replaceState(null, "", url);
}

/** Link that opens the game straight on this room's code. */
export function inviteLink(code: string): string {
  return `${location.origin}${location.pathname}?r=${code}`;
}

function asProfile(value: string | null): ResolutionProfile | null {
  return RESOLUTION_PROFILES.find((p) => p === value) ?? null;
}

function numberParam(params: URLSearchParams, key: string): number {
  const value = Number(params.get(key));
  return Number.isFinite(value) && value > 0 ? value : 0;
}

// Storage can throw (private mode, blocked site data): it is only a convenience.
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
